import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { apiClient } from '../../utils/apiClient';

interface RegistrationData {
  tempToken: string;
  authType: 'wallet' | 'twitter';
  identifier: string;
  suggestedName: string;
  twitterHandle?: string;
  profileImageUrl?: string;
}

/**
 * Registration screen for new users.
 * Displayed after wallet signature or Twitter OAuth verification.
 */
export function RegistrationScreen() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Parse URL params
  const [registrationData, setRegistrationData] = useState<RegistrationData | null>(null);
  const [displayName, setDisplayName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [canSubmit, setCanSubmit] = useState(false);

  // Parse registration data from URL
  useEffect(() => {
    const tempToken = searchParams.get('temp_token');
    const authType = searchParams.get('auth_type') as 'wallet' | 'twitter';
    const identifier = searchParams.get('identifier');
    const suggestedName = searchParams.get('suggested_name');
    const twitterHandle = searchParams.get('twitter_handle');
    const profileImageUrl = searchParams.get('profile_image_url');

    if (!tempToken || !authType || !identifier || !suggestedName) {
      setError('Invalid registration link. Please try signing in again.');
      return;
    }

    setRegistrationData({
      tempToken,
      authType,
      identifier,
      suggestedName,
      twitterHandle: twitterHandle || undefined,
      profileImageUrl: profileImageUrl || undefined,
    });

    setDisplayName(suggestedName);
  }, [searchParams]);

  // Enable submit button after delay (prevents accidental clicks)
  useEffect(() => {
    const timer = setTimeout(() => setCanSubmit(true), 1500);
    return () => clearTimeout(timer);
  }, []);

  const handleSubmit = async () => {
    if (!registrationData || !canSubmit) return;

    setIsSubmitting(true);
    setError(null);

    try {
      const response = await apiClient.register(
        registrationData.tempToken,
        registrationData.authType === 'wallet' ? displayName : undefined
      );

      // Store auth data
      const authData = {
        token: response.token,
        userId: response.user.id,
        displayName: response.user.display_name,
        authType: response.user.auth_type,
        walletAddress: response.user.wallet_address,
        twitterHandle: response.user.twitter_handle,
        profileImageUrl: response.user.profile_image_url,
        expiresAt: Date.now() + (response.expires_in * 1000),
      };
      localStorage.setItem('mfersarcade_auth', JSON.stringify(authData));

      // Notify other components
      window.dispatchEvent(new CustomEvent('auth-storage-update'));

      // Redirect to home
      navigate('/');

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Registration failed');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate('/');
  };

  // Error state
  if (error && !registrationData) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.errorIcon}>!</div>
          <h2 style={styles.title}>Registration Error</h2>
          <p style={styles.errorText}>{error}</p>
          <button style={styles.primaryButton} onClick={() => navigate('/')}>
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  // Loading state
  if (!registrationData) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.spinner} />
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  const isTwitterAuth = registrationData.authType === 'twitter';
  const isWalletAuth = registrationData.authType === 'wallet';

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        {/* Header */}
        <div style={styles.header}>
          <span style={styles.headerEmoji}>MFERS ARCADE</span>
          <h1 style={styles.title}>Create Your Account</h1>
        </div>

        {/* Auth type indicator */}
        <div style={styles.authIndicator}>
          {isTwitterAuth ? (
            <>
              {registrationData.profileImageUrl && (
                <img
                  src={registrationData.profileImageUrl}
                  alt=""
                  style={styles.profileImage}
                />
              )}
              <span style={styles.authLabel}>
                Signing in as @{registrationData.twitterHandle}
              </span>
            </>
          ) : (
            <>
              <span style={styles.walletIcon}>*</span>
              <span style={styles.authLabel}>
                {truncateAddress(registrationData.identifier)}
              </span>
            </>
          )}
        </div>

        {/* Display name input */}
        <div style={styles.formGroup}>
          <label style={styles.label}>Display Name</label>
          {isWalletAuth ? (
            <>
              <input
                type="text"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                maxLength={20}
                style={styles.input}
                placeholder="Enter your display name"
              />
              <span style={styles.hint}>
                2-20 characters. This is how you'll appear on leaderboards.
              </span>
            </>
          ) : (
            <>
              <input
                type="text"
                value={displayName}
                readOnly
                style={{ ...styles.input, ...styles.inputReadOnly }}
              />
              <span style={styles.hint}>
                Your X display name will be used.
              </span>
            </>
          )}
        </div>

        {/* Error message */}
        {error && (
          <div style={styles.errorMessage}>
            {error}
          </div>
        )}

        {/* Buttons */}
        <div style={styles.buttonRow}>
          <button
            style={styles.cancelButton}
            onClick={handleCancel}
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            style={{
              ...styles.primaryButton,
              ...((!canSubmit || isSubmitting) ? styles.buttonDisabled : {}),
            }}
            onClick={handleSubmit}
            disabled={!canSubmit || isSubmitting}
          >
            {isSubmitting ? 'Creating...' : 'Create Account'}
          </button>
        </div>

        {/* Terms notice */}
        <p style={styles.terms}>
          By creating an account, you agree to play nice and have fun.
        </p>
      </div>
    </div>
  );
}

function truncateAddress(address: string): string {
  if (!address || address.length < 10) return address;
  return `${address.slice(0, 6)}...${address.slice(-4)}`;
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#FEAC64',
    padding: '20px',
  },
  card: {
    backgroundColor: '#fff',
    borderRadius: '16px',
    padding: '32px',
    maxWidth: '400px',
    width: '100%',
    textAlign: 'center',
    boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
  },
  header: {
    marginBottom: '24px',
  },
  headerEmoji: {
    fontSize: '24px',
    display: 'block',
    marginBottom: '12px',
    fontFamily: 'monospace',
    letterSpacing: '2px',
    color: '#000',
  },
  title: {
    color: '#000',
    fontSize: '24px',
    margin: 0,
  },
  authIndicator: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '12px',
    backgroundColor: 'rgba(0,0,0,0.05)',
    borderRadius: '12px',
    padding: '12px 16px',
    marginBottom: '24px',
  },
  profileImage: {
    width: '32px',
    height: '32px',
    borderRadius: '50%',
  },
  walletIcon: {
    fontSize: '24px',
  },
  authLabel: {
    color: '#555',
    fontSize: '14px',
  },
  formGroup: {
    marginBottom: '24px',
    textAlign: 'left',
  },
  label: {
    display: 'block',
    color: '#000',
    fontSize: '14px',
    fontWeight: 'bold',
    marginBottom: '8px',
  },
  input: {
    width: '100%',
    padding: '12px 16px',
    fontSize: '16px',
    backgroundColor: '#fff',
    border: '2px solid #333',
    borderRadius: '8px',
    color: '#000',
    boxSizing: 'border-box' as const,
  },
  inputReadOnly: {
    backgroundColor: '#eee',
    color: '#666',
    cursor: 'not-allowed',
  },
  hint: {
    display: 'block',
    color: '#666',
    fontSize: '12px',
    marginTop: '6px',
  },
  buttonRow: {
    display: 'flex',
    gap: '12px',
  },
  primaryButton: {
    flex: 1,
    backgroundColor: '#000',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    padding: '14px 24px',
    fontSize: '16px',
    fontWeight: 'bold',
    cursor: 'pointer',
  },
  cancelButton: {
    flex: 1,
    backgroundColor: 'transparent',
    color: '#333',
    border: '2px solid #333',
    borderRadius: '8px',
    padding: '14px 24px',
    fontSize: '16px',
    cursor: 'pointer',
  },
  buttonDisabled: {
    opacity: 0.5,
    cursor: 'not-allowed',
  },
  errorMessage: {
    backgroundColor: 'rgba(239, 68, 68, 0.1)',
    border: '1px solid #ef4444',
    borderRadius: '8px',
    padding: '12px',
    color: '#ef4444',
    fontSize: '14px',
    marginBottom: '16px',
  },
  errorIcon: {
    fontSize: '48px',
    marginBottom: '12px',
    color: '#ef4444',
    fontWeight: 'bold',
  },
  errorText: {
    color: '#666',
    marginBottom: '24px',
  },
  spinner: {
    width: '32px',
    height: '32px',
    border: '3px solid #ddd',
    borderTop: '3px solid #000',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
    margin: '0 auto 16px',
  },
  terms: {
    color: '#666',
    fontSize: '12px',
    marginTop: '24px',
    marginBottom: 0,
  },
};

export default RegistrationScreen;
